import { Box, Collapse, Slide } from '@mui/material'
import cn from 'classnames'
import { Location } from 'history'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SidenavContentExpandedItemLink, {
  SidenavContentExpandedItemLinkProps,
} from 'src/components/SidenavContentExpandedItemLink/SidenavContentExpandedItemLink'
import SidenavSelectedBar from 'src/components/SidenavSelectedBar/SidenavSelectedBar'
import SvgDarkChevronClosed from 'src/components/Svgs/SvgDarkChevronClosed'
import SvgNavAlertCircle from 'src/components/Svgs/SvgNavAlertCircle'
import useSidenavItemsExpanded from 'src/context/SidenavItemsExpandedContext/useSidenavItemsExpanded'
import { SupportedBrands } from 'src/enums'
import { LocationState } from 'src/redux/createRootReducer'
import { selectBrandName } from 'src/redux/selectors/session.selectors'

import style from './SidenavContentNavItem.style'

interface SidenavContentNavItemProps {
  ActiveSvg: React.ReactElement
  InactiveSvg: React.ReactElement
  isSidenavOpen: boolean
  isActive: boolean
  label: string
  itemType?: 'navItem' | 'button'
  id: string
  to?: string | Location<LocationState>
  href?: string
  notificationsCount?: number
  onClick?: () => void
  expandOptions?: SidenavContentExpandedItemLinkProps[]
}

const SidenavContentNavItem: React.FC<SidenavContentNavItemProps> = (
  props,
): React.ReactElement => {
  const { itemType = 'navItem' } = props

  const { isExpanded, toggleExpanded } = useSidenavItemsExpanded()
  const expanded = isExpanded(props.id)
  const isJPM = useSelector(selectBrandName) === SupportedBrands.Jpm

  useEffect(() => {
    if (props.expandOptions && props.isActive && !expanded) {
      toggleExpanded(props.id)
    }
  }, [props.expandOptions, props.isActive, expanded])

  return (
    <div
      css={style}
      className={cn({
        activeExpanded: props.isActive && expanded && props.isSidenavOpen,
      })}
    >
      <div className="sidenav-wrapper">
        <div className="sidenav-content">
          <Slide
            in={props.isSidenavOpen && props.isActive && !expanded}
            direction={'right'}
            mountOnEnter
            unmountOnExit
          >
            <SidenavSelectedBar />
          </Slide>
          <HoverableLink
            testId={props.id}
            className={cn('sidenav-item', {
              'sidenav-open': props.isSidenavOpen,
              'sidenav-closed': !props.isSidenavOpen,
              active: props.isActive,
              expanded: expanded,
              jpm: isJPM,
            })}
            ariaLabel={props.label}
            to={props.to}
            onClick={() => {
              if (props.expandOptions) {
                toggleExpanded(props.id)
              }
              props.onClick?.()
            }}
            id={props.id}
            href={props.href}
          >
            <div className="sidenav-icon">
              {props.isActive ? props.ActiveSvg : props.InactiveSvg}
              {props.notificationsCount && props.notificationsCount > 0 ? (
                <SvgNavAlertCircle
                  className={cn('sidenav-notification-icon', {
                    'sidenav-open': props.isSidenavOpen,
                    'sidenav-closed': !props.isSidenavOpen,
                  })}
                />
              ) : (
                <></>
              )}
            </div>
            <div className="sidenav-label-container">
              <span
                className={cn('sidenav-label', itemType, {
                  active: props.isActive,
                  inactive: !props.isActive,
                })}
              >
                {props.label}
              </span>
            </div>
            {props.notificationsCount !== undefined &&
              props.notificationsCount > 0 && (
                <div
                  className={cn('sidenav-notification-badge', {
                    'sidenav-open': props.isSidenavOpen,
                    'sidenav-closed': !props.isSidenavOpen,
                  })}
                >
                  <div className="sidenav-notification-badge-inner">
                    {props.notificationsCount < 99
                      ? props.notificationsCount
                      : '99+'}
                  </div>
                </div>
              )}
            {props.expandOptions && (
              <div
                className={cn('sidenav-chevron', {
                  'sidenav-open': props.isSidenavOpen,
                  'sidenav-closed': !props.isSidenavOpen,
                })}
              >
                <SvgDarkChevronClosed className={cn('icon', { expanded })} />
              </div>
            )}
          </HoverableLink>
        </div>
      </div>
      {props.expandOptions && (
        <Collapse
          in={expanded && props.isSidenavOpen}
          sx={{ overflow: 'hidden' }}
          data-testid={`navgroup-${props.id}`}
          data-isexpanded={`${expanded && props.isSidenavOpen}`}
        >
          <Box className="sidenav-expanded-container">
            {props.expandOptions.map((option, index) => (
              <SidenavContentExpandedItemLink key={index} {...option} />
            ))}
          </Box>
        </Collapse>
      )}
    </div>
  )
}

export default SidenavContentNavItem
